import { fetchUser } from '@/api/user';
import { callPostApi } from '@/components/api-common';
import {
  Product,
  PaymentSource,
  Invoice,
  EstimateResult,
  Subscription,
  Interval,
  Coupon,
  Phase,
} from './models';

async function getAllProducts() {
  const res = (await callPostApi(
    '/user/service/subs/subs_products/'
  )) as Product[];
  const FREE_PRODUCT: Product = {
    id: 'Free',
    level_name: 'FREE',
    name: 'Free',
    stripe_id: '',
    plans: [],
  };
  return [FREE_PRODUCT, ...res];
}

export interface CouponInfo {
  coupon?: {
    id: string;
  };
}
async function getCoupons() {
  return callPostApi(
    '/user/service/subs/prefill_coupon/'
  ) as Promise<CouponInfo>;
}
async function getPaymentResources() {
  const res = (await callPostApi(
    '/user/service/subs/payment/payment_methods/'
  )) as PaymentSource[];
  return res;
}

async function getUpcomingInvoice() {
  const res = (await callPostApi(
    'user/service/subs/invoice/upcoming/'
  )) as Invoice;
  return res;
}
async function estimateInvoice(payload: {
  level_name: string;
  interval: Interval;
  quantity: number;
  coupon?: string;
  company?: string;
}) {
  const res = (await callPostApi(
    'user/service/subs/invoice/estimate/',
    payload
  )) as EstimateResult;
  return res;
}

async function getSubscriptions() {
  const res = (await callPostApi(
    'user/service/subs/subscriptions/'
  )) as Subscription[];
  return res;
}

async function getBillingSubscriptions() {
  const res = (await callPostApi(
    'user/service/subs/billing_subscriptions/'
  )) as Subscription[];
  return res;
}

export type PreviewSchedue = {
  current: Phase;
  now: Phase | null; // 立即生效的subscription （upgrade部分）
  next: Phase | null; // 下个周期会生效的subscription，可能为null（只有upgrade时），为空则表示和now相同
};

async function previewSchedule(payload: {
  level_name: string;
  quantity: number;
  interval: Interval;
  coupon: string;
  company_name?: string;
}) {
  const res = await callPostApi('user/service/subs/schedule/preview/', payload);
  return res as PreviewSchedue;
}

async function deleteSchedule() {
  const res = await callPostApi('user/service/subs/schedule/delete/');
  return res;
}

async function createOrUpdateSubscription(payload: {
  level_name?: string;
  quantity?: number;
  interval?: Interval;
  coupon?: string;
  company_name?: string;
  subscription_proration_date?: string | null;
  from_samsung_store?: boolean;
}) {
  const res = await callPostApi(
    'user/service/subs/subscription/create_or_update/',
    payload
  );
  return res as Subscription;
}
async function cancelSubscription(params?: {
  reason?: string;
  other_reason?: string;
}) {
  const res = await callPostApi(
    'user/service/subs/subscription/cancel/',
    params || {}
  );
  return res;
}

async function renew() {
  const res = await callPostApi('user/service/subs/subscription/dont_cancel/');
  return res;
}
async function retryPay(invoiceId: string) {
  const res = await callPostApi(
    'user/service/subs/invoice/retry_for_failing/',
    {
      invoice_id: invoiceId,
    }
  );
  return res;
}

async function getLatestFailedInvoice(): Promise<Invoice> {
  const res = (await callPostApi(
    'user/service/subs/invoice/latest_failed/'
  )) as Invoice;
  return res;
}
async function getUser() {
  return await fetchUser();
}

async function validateCoupon(coupon: string) {
  return (await callPostApi('user/service/subs/coupon/', { coupon })) as Coupon;
}

async function getStripeSetupIntentScrete() {
  return await callPostApi<string>(
    'user/service/subs/stripe/setup_intent/client_secret/'
  );
}

async function setStripeDefaultPaymentMethod(payment_method_id: string) {
  return await callPostApi(
    'user/service/subs/stripe/set_default_payment_method/',
    { payment_method_id }
  );
}

export function createCheckoutSession(payload: {
  price: string;
  coupon?: string;
  quantity: number;
  company_name: string;
  from_samsung_store?: boolean;
}) {
  return callPostApi(
    'user/service/subs/stripe/create_checkout_session/',
    payload
  ) as Promise<{ client_secret: string }>;
}

export async function directPaySubscriptionUpgradeRequest() {
  return await callPostApi('user/service/subs/direct_pay_upgrade_request/', {});
}

export default {
  validateCoupon,
  getAllProducts,
  getCoupons,
  getPaymentResources,
  getUpcomingInvoice,
  estimateInvoice,
  getSubscriptions,
  getBillingSubscriptions,
  createOrUpdateSubscription,
  cancelSubscription,
  renew,
  retryPay,
  getUser,
  previewSchedule,
  deleteSchedule,
  getStripeSetupIntentScrete,
  setStripeDefaultPaymentMethod,
  getLatestFailedInvoice,
  directPaySubscriptionUpgradeRequest,
};
